/**
 * SSE client for streaming app execution.
 * Handles real-time updates from the embed execution endpoint.
 */

import type { SSEEvent } from '../types'
import { EmbedApiError, toApiError, toNetworkError } from './api'

export interface SSEClientOptions {
  onEvent: (event: SSEEvent) => void
  onError: (error: Error) => void
  onComplete: () => void
}

/**
 * Execute an app with streaming SSE updates.
 *
 * @param url - The streaming endpoint URL (with token in query param)
 * @param inputs - Input values for the app
 * @param options - Callbacks for events
 * @returns AbortController to cancel the stream
 */
export function executeAppStream(
  url: string,
  inputs: Record<string, unknown>,
  options: SSEClientOptions
): AbortController {
  const controller = new AbortController()

  // Start the streaming request
  ;(async () => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({ inputs }),
        signal: controller.signal,
      })

      // The run door answers a rejected origin or a revoked token here too, and
      // its detail is the only thing that says which. Raw response text would
      // reach the user as a JSON blob, or as an HTML page when baseUrl is wrong.
      if (!response.ok) {
        throw await toApiError(response, url)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body')
      }

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          // Skip empty lines and comments
          if (!line || line.startsWith(':')) continue

          // Parse SSE data lines
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim()
            if (data) {
              try {
                const event = JSON.parse(data) as SSEEvent
                options.onEvent(event)

                // Check for terminal events
                if (event.type === 'flow_complete' || event.type === 'flow_error') {
                  options.onComplete()
                  return
                }
              } catch {
                console.warn('AgentBase: Failed to parse SSE event:', data)
              }
            }
          }
        }
      }

      // Stream ended without terminal event
      options.onComplete()
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return
      }
      if (error instanceof EmbedApiError) {
        options.onError(error)
        return
      }
      // A TypeError from fetch here is the browser refusing the request — no
      // network, or CORS. Saying so beats "Failed to fetch".
      options.onError(toNetworkError(url))
    }
  })()

  return controller
}
